import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { PermanentProxy } from '../../types/api';
import { formatDate } from '../../utils/dateFormatter';

interface ProxyCardProps {
  proxy: PermanentProxy;
}

export function ProxyCard({ proxy }: ProxyCardProps) {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'expired':
        return 'bg-red-500/20 text-red-400 border-red-500/50';
      case 'cancelled':
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
      default:
        return 'bg-neutral-medium/20 text-neutral-medium border-neutral-medium/50';
    }
  };

  const getServerLabel = () => {
    // Server ID is not in the proxy object, but we can infer from type or other fields
    // For now, we'll just show the type
    return proxy.type || t('proxies.unknownServer');
  };

  return (
    <Link
      to={`/proxies/${proxy._id}`}
      className="block bg-neutral-dark/50 backdrop-blur-lg rounded-xl shadow-lg border border-neutral-dark p-4 md:p-6 hover:border-brand-accent/50 transition-all duration-300 overflow-hidden"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h3 className="text-sm sm:text-lg font-heading font-semibold text-neutral-light break-all leading-snug">
              {proxy.ipAddress}:{proxy.port}
            </h3>
            {proxy.port_socks && (
              <span className="text-xs sm:text-sm text-neutral-medium font-body break-all">
                (SOCKS: {proxy.port_socks})
              </span>
            )}
          </div>
          <p className="text-neutral-medium font-body text-sm mb-2 break-words">{proxy.location}</p>
          {proxy.city && (
            <p className="text-neutral-medium font-body text-sm mb-2 break-words">{proxy.city}</p>
          )}
          <p className="text-neutral-medium font-body text-sm break-words">{getServerLabel()}</p>
        </div>
        <span
          className={`px-2.5 py-1 rounded-full text-xs font-body font-semibold border whitespace-nowrap ${getStatusColor(proxy.status)}`}
        >
          {t(`proxies.status.${proxy.status}`)}
        </span>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <div className="text-neutral-medium font-body break-words">
          {t('proxies.details.expiresAt')}: {formatDate(proxy.expiresAt, 'PP h:mm a')}
        </div>
        <div className="text-brand-accent font-body font-semibold shrink-0">
          ${proxy.price.toFixed(2)}
        </div>
      </div>
    </Link>
  );
}

